import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CaseSummary, Paginated } from '@foundation/shared';
import { CASE_PRIORITY_LABEL_TH, CASE_STATUS_LABEL_TH } from '@foundation/shared';
import { api, buildQuery } from '../lib/api';
import { formatDate } from '../lib/format';
import { useProfile } from '../auth';

/** เคสของฉันที่ยังต้องทำอะไรต่อ — เจ้าหน้าที่ภาคสนามเปิดจาก rich menu */
export default function MyCases() {
  const profile = useProfile();
  const [rows, setRows] = useState<CaseSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    api<Paginated<CaseSummary>>(
      `/cases${buildQuery({ assignedStaffId: profile.id, limit: 50, sort: 'priority' })}`,
      { signal: ac.signal },
    )
      .then((r) => setRows(r.data))
      .catch((e: unknown) => {
        if (!ac.signal.aborted) setError(e instanceof Error ? e.message : 'โหลดเคสไม่สำเร็จ');
      });
    return () => ac.abort();
  }, [profile.id]);

  // เคสที่จบแล้วไม่ต้องเด่น แต่ยังให้เลื่อนไปดูได้
  const open = (rows ?? []).filter(
    (c) => !['completed', 'cancelled', 'rejected'].includes(c.status),
  );
  const closed = (rows ?? []).filter((c) =>
    ['completed', 'cancelled', 'rejected'].includes(c.status),
  );

  return (
    <div className="screen">
      <div className="topbar">
        <h1>เคสของฉัน</h1>
      </div>

      {error && <div className="alert error">{error}</div>}

      {rows === null ? (
        <div className="center">กำลังโหลด…</div>
      ) : open.length === 0 && closed.length === 0 ? (
        <div className="empty">ยังไม่มีเคสที่คุณรับผิดชอบ</div>
      ) : (
        <>
          {open.map((c) => (
            <Link key={c.id} to={`/case/${c.id}`} className="card row-link">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <strong>{c.caseCode}</strong>
                <span className={`badge ${c.isOverdue ? 'red' : ''}`}>
                  {CASE_STATUS_LABEL_TH[c.status]}
                </span>
              </div>
              <div>{c.title}</div>
              <div className="muted small">
                {c.beneficiaryName} · {c.aidTypeName}
                {c.priority === 'urgent' || c.priority === 'high'
                  ? ` · ${CASE_PRIORITY_LABEL_TH[c.priority]}`
                  : ''}
              </div>
              {c.approvedAmount !== null && (
                <div className="muted small">
                  อนุมัติ {(c.approvedAmount / 100).toLocaleString('th-TH')} บาท · จ่ายแล้ว{' '}
                  {(c.disbursedAmount / 100).toLocaleString('th-TH')} บาท
                </div>
              )}
              {c.dueDate && (
                <div className="muted small">กำหนดเสร็จ {formatDate(c.dueDate)}</div>
              )}
            </Link>
          ))}

          {closed.length > 0 && (
            <>
              <h2 className="muted small" style={{ marginTop: 20 }}>
                ปิดแล้ว {closed.length} เคส
              </h2>
              {closed.map((c) => (
                <Link key={c.id} to={`/case/${c.id}`} className="card row-link muted">
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span>{c.caseCode}</span>
                    <span className="badge">{CASE_STATUS_LABEL_TH[c.status]}</span>
                  </div>
                  <div className="small">{c.title}</div>
                </Link>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}
